/* Boxes over the picture: the hands and the objects named in the sub-task under
 * the playhead (31 Oct 2026, the manager's request). See lib/highlights.ts for
 * the rules and api/highlights.py for where the boxes come from.
 *
 * ITS OWN rAF, NOT THE PLAYER'S 15Hz PUSH. Boxes on a moving hand at 15Hz lag
 * visibly behind the picture; the canvas reads `currentTime` itself at display
 * rate and touches no React state while it runs, so the workspace does not
 * re-render (see Player's comment on why the playhead is not state).
 *
 * pointer-events: none — a click on the picture must still play / pause. */

import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import type { Highlights, Subtask } from "../api/types";
import { ROLE_COLOR, boxAt, containRect, segmentFor } from "../lib/highlights";

interface Props {
  video: RefObject<HTMLVideoElement | null>;
  highlights: Highlights | null;
  subtasks: readonly Subtask[];
}

export function HighlightOverlay({ video, highlights, subtasks }: Props) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [stale, setStale] = useState(false);
  const staleRef = useRef(false);

  useEffect(() => {
    let raf = 0;
    const subs = [...subtasks].sort((a, b) => a.start_seconds - b.start_seconds);
    const draw = () => {
      const c = canvas.current, v = video.current;
      if (c && v) {
        const dpr = window.devicePixelRatio || 1;
        const W = c.clientWidth, H = c.clientHeight;
        if (c.width !== Math.round(W * dpr) || c.height !== Math.round(H * dpr)) {
          c.width = Math.round(W * dpr); c.height = Math.round(H * dpr);
        }
        const g = c.getContext("2d");
        if (g) {
          g.setTransform(dpr, 0, 0, dpr, 0, 0);
          g.clearRect(0, 0, W, H);
          const t = v.currentTime;
          const sub = subs.find((s) => s.start_seconds <= t && t < s.end_seconds) ?? null;
          const m = segmentFor(highlights, sub);
          const isStale = m.kind === "stale";
          if (isStale !== staleRef.current) { staleRef.current = isStale; setStale(isStale); }
          if (m.kind === "match") {
            const [ox, oy, pw, ph] = containRect(W, H, v.videoWidth, v.videoHeight);
            for (const tr of m.segment.tracks) {
              const b = boxAt(tr, t);
              if (!b) continue;
              const col = ROLE_COLOR[tr.role];
              const x = ox + b[0] * pw, y = oy + b[1] * ph, w = (b[2] - b[0]) * pw, h = (b[3] - b[1]) * ph;
              g.fillStyle = col + "26";                // ~15% fill
              g.fillRect(x, y, w, h);
              g.lineWidth = 2; g.strokeStyle = col;
              g.strokeRect(x, y, w, h);
              // NO MOVEMENT TRAIL (2 Nov 2026, manager review): boxes and labels only.
              // lib/highlights.trailAt is kept, unused, in case it is wanted back.
              const label = tr.role === "hand" ? "Hand" : tr.name.charAt(0).toUpperCase() + tr.name.slice(1);
              g.font = "600 12px 'Plus Jakarta Sans Variable', system-ui, sans-serif";
              const tw = g.measureText(label).width + 10;
              const ly = Math.max(0, y - 18);
              g.fillStyle = col; g.fillRect(x, ly, tw, 18);
              g.fillStyle = "#fff"; g.fillText(label, x + 5, ly + 13);
            }
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [video, highlights, subtasks]);

  return (
    <>
      <canvas ref={canvas} className="pointer-events-none absolute inset-0 h-full w-full"
        aria-hidden="true" />
      {stale && (
        <div className="pointer-events-none absolute right-3 top-12 rounded-chip bg-navy9/80
          px-2 py-1 text-[11px] text-white/80">
          Highlights hidden: this sub-task was edited after detection
        </div>
      )}
    </>
  );
}
